import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

import { getFailureContext } from '../cacheBridgeCommon.ts';
import { reportCacheBridgeFailure } from '../cacheBridgeDiagnostics.ts';
import { getApolloRelayCacheBridgeDisabledReason } from '../cacheBridgeGate.ts';
import { syncQueryCacheBridge } from '../queryCacheBridgeSync.ts';
import { getApolloClient } from '../utils/getApolloClient.ts';
import { toDocumentNode } from '../../toDocumentNode.ts';

type RefetchQuerySyncArgs<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	data: TData;
	useAggClient?: boolean;
};

type ApolloRefetchQueryEntry = {
	query: GraphQLTaggedNode;
	variables?: Record<string, unknown>;
};

type ApolloRefetchQueryClient = {
	readQuery<TData>(options: { query: unknown; variables?: Record<string, unknown> }): TData | null;
};

const APOLLO_FIRE_AND_FORGET_REFETCH_SYNC_ATTEMPTS = 5;
const APOLLO_FIRE_AND_FORGET_REFETCH_SYNC_DELAY_MS = 25;

// Relay-route mutation refetches are still query results. Mirror successful
// refetch data through the existing query bridge, but never let bridge failures
// change the original refetch promise's resolve/reject behavior.
function syncRelayRefetchQueryResultSafely<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(args: RefetchQuerySyncArgs<TData, TVariables>): void {
	try {
		syncQueryCacheBridge({
			source: 'relay',
			query: args.query,
			variables: args.variables,
			data: args.data,
			useAggClient: args.useAggClient,
		});
	} catch (error) {
		reportCacheBridgeFailure(error, getFailureContext('query', 'write', 'apollo'));
	}
}

export function syncRelayRefetchQueryPromise<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(
	refetchPromise: Promise<TData>,
	args: Omit<RefetchQuerySyncArgs<TData, TVariables>, 'data'>,
): Promise<TData> {
	return refetchPromise.then((data) => {
		syncRelayRefetchQueryResultSafely({
			...args,
			data,
		});
		return data;
	});
}

function isApolloRefetchQueryEntry(entry: unknown): entry is ApolloRefetchQueryEntry {
	return Boolean(
		entry && typeof entry === 'object' && 'query' in entry && (entry as { query?: unknown }).query,
	);
}

function readApolloRefetchQueryData<TData>({
	client,
	entry,
}: {
	client: ApolloRefetchQueryClient;
	entry: ApolloRefetchQueryEntry;
}): TData | null {
	return client.readQuery<TData>({
		query: toDocumentNode(entry.query),
		variables: entry.variables,
	});
}

function syncApolloRefetchQueryEntryFromCache({
	client,
	entry,
	useAggClient,
	reportFinalFailure,
}: {
	client: ApolloRefetchQueryClient;
	entry: ApolloRefetchQueryEntry;
	useAggClient?: boolean;
	reportFinalFailure: boolean;
}): boolean {
	try {
		const data = readApolloRefetchQueryData<unknown>({ client, entry });
		if (data == null) return false;

		syncQueryCacheBridge({
			source: 'apollo',
			query: entry.query,
			variables: entry.variables,
			data,
			useAggClient,
		});
		return true;
	} catch (error) {
		if (reportFinalFailure) {
			reportCacheBridgeFailure(error, getFailureContext('query', 'write', 'relay'));
		}
		return false;
	}
}

function syncApolloRefetchQueriesFromCacheOnce({
	entries,
	useAggClient,
	reportFinalFailure,
}: {
	entries?: readonly unknown[];
	useAggClient?: boolean;
	reportFinalFailure: boolean;
}): ApolloRefetchQueryEntry[] {
	const client = getApolloClient<ApolloRefetchQueryClient>({ useAggClient });
	if (!client) return [];

	const pendingEntries: ApolloRefetchQueryEntry[] = [];
	for (const entry of entries ?? []) {
		// Apollo still supports string query names, but those do not contain the
		// Relay artifact needed to mirror the result into Relay.
		if (!isApolloRefetchQueryEntry(entry)) continue;

		const didSync = syncApolloRefetchQueryEntryFromCache({
			client,
			entry,
			useAggClient,
			reportFinalFailure,
		});
		if (!didSync) {
			pendingEntries.push(entry);
		}
	}

	return pendingEntries;
}

function scheduleApolloRefetchQueriesFromCacheSync({
	entries,
	useAggClient,
	remainingAttempts,
}: {
	entries: ApolloRefetchQueryEntry[];
	useAggClient?: boolean;
	remainingAttempts: number;
}): void {
	if (entries.length === 0) return;

	setTimeout(() => {
		const pendingEntries = syncApolloRefetchQueriesFromCacheOnce({
			entries,
			useAggClient,
			reportFinalFailure: remainingAttempts <= 1,
		});

		if (pendingEntries.length > 0 && remainingAttempts > 1) {
			scheduleApolloRefetchQueriesFromCacheSync({
				entries: pendingEntries,
				useAggClient,
				remainingAttempts: remainingAttempts - 1,
			});
		}
	}, APOLLO_FIRE_AND_FORGET_REFETCH_SYNC_DELAY_MS);
}

export function syncApolloRefetchQueriesFromCache({
	entries,
	awaitRefetchQueries,
	useAggClient,
}: {
	entries?: readonly unknown[];
	awaitRefetchQueries?: boolean;
	useAggClient?: boolean;
}): void {
	if (!entries?.length) return;

	// Avoid source-cache reads when the bridge is disabled. The actual sync write
	// is still guarded by syncQueryCacheBridge, but this keeps disabled bridge
	// work effectively at zero.
	if (getApolloRelayCacheBridgeDisabledReason()) return;

	const pendingEntries = syncApolloRefetchQueriesFromCacheOnce({
		entries,
		useAggClient,
		reportFinalFailure: Boolean(awaitRefetchQueries),
	});

	// Native Apollo does not await fire-and-forget refetches, so their cache
	// writes may happen shortly after the mutation promise resolves. Retry the
	// source-cache read briefly; this observes Apollo's write without issuing a
	// second network request.
	if (!awaitRefetchQueries && pendingEntries.length > 0) {
		scheduleApolloRefetchQueriesFromCacheSync({
			entries: pendingEntries,
			useAggClient,
			remainingAttempts: APOLLO_FIRE_AND_FORGET_REFETCH_SYNC_ATTEMPTS,
		});
	}
}
